/** Supervisor / office map (spec §9 FREE-001…003): every open paper, filterable by server, with rule eligibility. */
import { useMemo, useState } from 'react';
import { addressKey, PIN_COLORS, PIN_LABELS, suggestStopOrder, type PinState } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDateTime } from '../shared/format.ts';
import { Link } from '../shared/router.tsx';
import { PinLegend, SlippyMap } from '../shared/SlippyMap.tsx';
import { Badge, Card, ErrorBox, Notice, Spinner, StatusBadge, useAction, useLoad } from '../shared/ui.tsx';

export interface MapPaper {
  id: string;
  civilosNumber: string;
  status: string;
  paperType: string;
  defendant: string;
  address: { line1: string; line2: string | null; city: string | null; state: string | null; zip: string | null };
  lat: number | null;
  lng: number | null;
  pinSource: 'corrected' | 'geocoded' | null;
  geocodeStatus: string;
  eligibleNow: boolean;
  reason: string | null;
  state: PinState;
  urgencyTier: string;
  priority: number;
  serverId: string | null;
  serverName: string | null;
  attemptCount: number;
}

const STATES: PinState[] = ['green', 'yellow', 'red', 'nsh', 'gray'];

export function OfficeMap() {
  const { can } = useAuth();
  const [serverId, setServerId] = useState('');
  const [onlyEligible, setOnlyEligible] = useState(false);
  const [showRoute, setShowRoute] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const meta = useLoad(() => api.get('/api/meta'));
  const data = useLoad(() => api.get<{ papers: MapPaper[]; missingCoordinates: number; generatedAt: string }>(`/api/map/papers?scope=open${serverId ? `&serverId=${serverId}` : ''}`), [serverId]);
  const a = useAction();
  const [queued, setQueued] = useState<number | null>(null);

  const papers = useMemo(() => (data.data?.papers ?? []).filter((p) => !onlyEligible || p.eligibleNow), [data.data, onlyEligible]);
  const located = papers.filter((p) => p.lat != null && p.lng != null);
  const missing = papers.filter((p) => p.lat == null || p.lng == null);

  const order = useMemo(
    () => (showRoute && serverId ? suggestStopOrder(located.filter((p) => p.eligibleNow).map((p) => ({ id: p.id, lat: p.lat!, lng: p.lng!, addressKey: addressKey(p.address), priority: p.priority })), null) : null),
    [showRoute, serverId, located],
  );
  const stopNo = new Map<string, number>();
  order?.stops.forEach((s, i) => s.paperIds.forEach((id) => stopNo.set(id, i + 1)));

  const counts = STATES.map((s) => ({ color: PIN_COLORS[s], label: PIN_LABELS[s], count: papers.filter((p) => p.state === s).length })).filter((x) => x.count > 0 || x.label === PIN_LABELS.nsh || x.label === PIN_LABELS.gray);
  const selected = papers.find((p) => p.id === sel) ?? null;
  const fitKey = `${serverId}|${onlyEligible}|${data.data?.generatedAt ?? ''}`;

  return (
    <div className="stack">
      <div className="row between wrap gap">
        <h1>Map</h1>
        <div className="row gap wrap">
          <label className="row gap-sm small">
            Server
            <select aria-label="Server" value={serverId} onChange={(e) => (setServerId(e.target.value), setSel(null))}>
              <option value="">All servers & unassigned</option>
              {meta.data?.servers.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.displayName ?? s.display_name}
                </option>
              ))}
            </select>
          </label>
          <label className="row gap-sm small">
            <input type="checkbox" checked={onlyEligible} onChange={(e) => setOnlyEligible(e.target.checked)} /> Eligible now only
          </label>
          <label className="row gap-sm small" title={serverId ? '' : 'Choose a server first'}>
            <input type="checkbox" checked={showRoute} disabled={!serverId} onChange={(e) => setShowRoute(e.target.checked)} /> Suggested stop order
          </label>
          <button className="btn sm" onClick={data.reload}>
            Refresh
          </button>
        </div>
      </div>
      <ErrorBox error={data.error ?? a.error} />
      {data.loading && !data.data && <Spinner />}
      <PinLegend items={counts} />
      <div className="map-layout">
        <SlippyMap
          height={620}
          fitKey={fitKey}
          ariaLabel="Open papers map"
          markers={located.map((p) => ({
            id: p.id,
            lat: p.lat!,
            lng: p.lng!,
            color: PIN_COLORS[p.state],
            label: stopNo.has(p.id) ? String(stopNo.get(p.id)) : p.urgencyTier !== 'normal' ? '!' : undefined,
            title: `${p.civilosNumber} — ${p.defendant}, ${p.address.line1}. ${PIN_LABELS[p.state]}${p.reason ? `: ${p.reason}` : ''}`,
            diamond: true,
          selected: p.id === sel,
          }))}
          route={order?.stops.map((s) => ({ lat: s.lat, lng: s.lng }))}
          onMarkerClick={setSel}
        />
        <div className="map-side">
          {selected ? (
            <div className="map-popup" aria-live="polite">
              <div className="row between">
                <strong className="mono">{selected.civilosNumber}</strong>
                <StatusBadge status={selected.status} />
              </div>
              <div>
                <strong>{selected.defendant}</strong> · {selected.paperType}
              </div>
              <div className="muted small">{[selected.address.line1, selected.address.line2, selected.address.city].filter(Boolean).join(', ')}</div>
              <div className="row gap-sm wrap small">
                <span className="legend-dot" style={{ background: PIN_COLORS[selected.state] }} /> {PIN_LABELS[selected.state]}
                {selected.urgencyTier !== 'normal' && <Badge tone="bad">{selected.urgencyTier === 'supervisor_urgent' ? 'Urgent' : 'Priority'}</Badge>}
              </div>
              {selected.reason && <div className="small muted">{selected.reason}</div>}
              <div className="small">
                {selected.serverName ?? 'Unassigned'} · {selected.attemptCount} attempt(s)
                {selected.pinSource === 'corrected' && ' · corrected pin'}
              </div>
              <div className="row gap">
                <Link to={`/papers/${selected.id}`} className="btn sm primary">
                  Open paper
                </Link>
                <button className="btn sm ghost" onClick={() => setSel(null)}>
                  Close
                </button>
              </div>
            </div>
          ) : (
            <p className="muted small">Select a pin for details. Gray pins exist but can’t be attempted right now (rules, hold, or not assigned).</p>
          )}
          {order && (
            <Card title={`Suggested order · ${order.stops.length} stop(s) · ${order.totalMiles} mi straight-line`}>
              <ol className="stop-list">
                {order.stops.map((s, i) => {
                  const ps = s.paperIds.map((id) => papers.find((p) => p.id === id)!);
                  return (
                    <li key={s.addressKey}>
                      <button className={s.paperIds.includes(sel ?? '') ? 'active' : ''} onClick={() => setSel(s.paperIds[0]!)}>
                        <span className="stop-num" style={{ background: PIN_COLORS[ps[0]!.state] }}>
                          {i + 1}
                        </span>
                        <span className="grow">
                          <span>{ps[0]!.address.line1}</span>
                          <span className="muted small"> · {ps.map((p) => p.defendant).join(', ')}</span>
                          <span className="muted small"> · {s.legMiles} mi</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
              <p className="small muted">Nearest-next order with urgent papers breaking ties. Full RouteIQ optimization comes later.</p>
            </Card>
          )}
          {missing.length > 0 && (
            <Card title={`Not on the map (${missing.length})`}>
              <ul className="plain small">
                {missing.slice(0, 30).map((p) => (
                  <li key={p.id}>
                    <Link to={`/papers/${p.id}`}>{p.civilosNumber}</Link> — {p.address.line1}{' '}
                    <span className="muted">({p.geocodeStatus === 'no_match' ? 'address not found' : p.geocodeStatus === 'failed' ? 'geocoder unavailable' : p.geocodeStatus})</span>
                  </li>
                ))}
              </ul>
              {can('admin.config') && (
                <button
                  className="btn sm"
                  disabled={a.busy}
                  onClick={() =>
                    a.run(async () => {
                      const r = await api.post<{ queued: number }>('/api/admin/geocode-missing');
                      setQueued(r.queued);
                      setTimeout(data.reload, 2500);
                    })
                  }
                >
                  Look up missing addresses
                </button>
              )}
              {queued != null && <Notice tone="info">Looking up {queued} address(es). The map refreshes shortly.</Notice>}
              <p className="small muted">Open a paper and use “Set pin on map” to place one by hand.</p>
            </Card>
          )}
          <p className="small muted">Updated {fmtDateTime(data.data?.generatedAt)}</p>
        </div>
      </div>
    </div>
  );
}
